import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/EkahauByOokla.css";
import ekahauLogo from "../../assets/images/partners/Ekahau-by-Ookla.jpg";

/* ── Animated Counter ── */
function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}

function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Data ── */
const whyCards = [
  { icon: "🗺️", title: "Data-Driven RF Design", body: "Replace guesswork with scientific precision. Ekahau Pro simulates radio frequency propagation on imported floor plans, predicting signal strength, capacity, and coverage before a single cable is run." },
  { icon: "📡", title: "Ekahau Sidekick 2 Hardware", body: "The world's fastest Wi-Fi analyzer. Sidekick 2 captures 2.4GHz and 5GHz simultaneously, enabling ALG Data Guard to complete comprehensive site surveys in half the time of competing tools." },
  { icon: "🤖", title: "AI-Assisted AP Placement", body: "Ekahau's AI engine analyzes floor plans, detects wall materials, and recommends optimal access point locations — dramatically accelerating the initial design phase for any size facility." },
  { icon: "📋", title: "Professional Documentation", body: "Every project produces automated, professional-grade Wi-Fi design reports including coverage maps, channel plans, AP placement guides, and post-survey validation data." },
  { icon: "🔬", title: "Spectrum Analysis", body: "Real-time spectrum analysis identifies interference sources, rogue devices, and non-Wi-Fi interference that conventional surveys miss — ensuring clean, high-performance wireless environments." },
  { icon: "✅", title: "Post-Deployment Validation", body: "Deployment isn't complete until validated. Post-install surveys with Sidekick 2 confirm the live network matches design specifications — providing documented proof of performance." },
];

const tools = [
  {
    name: "Ekahau Pro",
    icon: "🗺️",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=700&q=80",
    desc: "The industry-standard Wi-Fi planning and site survey software. Ekahau Pro enables predictive RF design on accurate floor plans, AP placement optimization, and comprehensive coverage simulation with 3D propagation modeling.",
    features: ["Predictive design with 3D RF simulation", "AP placement optimization", "Coverage, capacity & channel planning", "Site survey with heatmaps", "Automated installation reports"],
  },
  {
    name: "Ekahau Sidekick 2",
    icon: "📡",
    img: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=700&q=80",
    desc: "The world's fastest Wi-Fi analyzer. Sidekick 2 captures dual-band data simultaneously with dedicated survey hardware, enabling faster, more accurate spectrum measurements than any software-only solution.",
    features: ["Simultaneous 2.4 & 5GHz capture", "Real-time spectrum analysis", "Channel utilization heatmaps", "Fastest survey hardware available", "Direct integration with Ekahau Pro"],
  },
  {
    name: "AI-Assisted Design",
    icon: "🤖",
    img: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=700&q=80",
    desc: "Ekahau's AI engine analyzes floor plans, detects wall types, and calculates optimal AP positions. ALG Data Guard engineers refine AI recommendations for site-specific requirements and client density targets.",
    features: ["AI floor plan analysis", "Automatic AP count estimation", "Wall material detection", "Coverage optimization", "Fast initial design for budgeting"],
  },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "Reliable Wi-Fi for medical devices, EHR access, RTLS asset tracking, and voice-over-Wi-Fi in clinical environments with strict coverage requirements." },
  { icon: "🏫", name: "Education", desc: "High-density Wi-Fi for classrooms, lecture halls, and campuses — supporting hundreds of simultaneous student connections without performance degradation." },
  { icon: "🏭", name: "Warehousing & Logistics", desc: "Industrial Wi-Fi for barcode scanners, AGVs, and inventory systems across large, RF-challenging warehouse environments with steel shelving and high ceilings." },
  { icon: "🏨", name: "Hospitality", desc: "Guest Wi-Fi and back-of-house operational networks for hotels, conference centers, and resorts — delivering seamless roaming across complex multi-building environments." },
  { icon: "🏢", name: "Corporate Office", desc: "Unified enterprise Wi-Fi for open-plan offices, meeting rooms, and multi-floor buildings supporting voice, video, and high-density device environments." },
  { icon: "🏛️", name: "Government", desc: "Secure wireless infrastructure for government facilities with classified zone separation, 802.1X authentication, and compliance with security frameworks." },
  { icon: "🛍️", name: "Retail", desc: "In-store Wi-Fi for POS systems, inventory management, customer engagement, and analytics — optimized for the RF challenges of dense retail environments." },
  { icon: "🏗️", name: "Construction & Events", desc: "Temporary and permanent wireless solutions for construction sites, stadiums, and event venues with rapidly changing RF environments." },
];

const workflowSteps = [
  { num: "1", title: "Import Floor Plans", desc: "We import your building floor plans, map wall materials, and set scale — creating an accurate RF propagation model of your facility." },
  { num: "2", title: "Predictive Design", desc: "Ekahau Pro simulates wireless coverage across your floor plan — optimizing AP placement, channels, and transmit power before hardware is purchased." },
  { num: "3", title: "Validation Survey", desc: "Pre-deployment walk testing confirms the physical environment matches the model and refines AP placement if needed." },
  { num: "4", title: "Deploy to Spec", desc: "ALG Data Guard engineers install access points precisely where design specifies — no guesswork, no assumptions." },
  { num: "5", title: "Post-Install Validation", desc: "A post-deployment survey with Ekahau Sidekick 2 confirms the live network matches design specifications with comprehensive documentation." },
];

const deploymentSteps = [
  { icon: "📋", title: "Requirements", desc: "Define coverage, capacity, device types, and application requirements." },
  { icon: "🗺️", title: "Floor Plans", desc: "Import and calibrate building plans with accurate wall materials." },
  { icon: "📐", title: "RF Design", desc: "Predictive simulation and AP placement optimization." },
  { icon: "🔍", title: "Pre-Survey", desc: "Walk testing and environment validation before installation." },
  { icon: "🚀", title: "Deployment", desc: "Precision installation by certified wireless engineers." },
  { icon: "✅", title: "Validation", desc: "Post-install survey and professional documentation delivery." },
];

const benefits = [
  { icon: "🎯", title: "Eliminate Dead Zones Permanently", desc: "Data-driven AP placement based on actual RF simulation — not rules of thumb — eliminates coverage gaps before deployment." },
  { icon: "💰", title: "Reduce Infrastructure Costs", desc: "Accurate predictive design prevents over-engineering. Clients typically save 15–25% on AP counts versus traditional survey methods." },
  { icon: "⏱️", title: "Faster Project Completion", desc: "Ekahau Sidekick 2's simultaneous dual-band capture and automated reporting reduces survey time by up to 50% versus conventional tools." },
  { icon: "📄", title: "Professional Documentation", desc: "Every project delivers executive-ready reports, installation drawings, and as-built documentation — reducing support burden post-deployment." },
];

const certifications = [
  { badge: "📡", name: "Ekahau Certified Wi-Fi Engineer", desc: "Professional RF design and site survey certification using Ekahau Pro and Sidekick 2" },
  { badge: "🏆", name: "CWNA — Certified Wireless Network Administrator", desc: "Vendor-neutral wireless networking certification from CWNP" },
  { badge: "⭐", name: "Ekahau Authorized Reseller", desc: "Authorized Ekahau software and hardware reseller and deployment partner" },
  { badge: "🔐", name: "CWSP Security Professional", desc: "Certified Wireless Security Professional for enterprise wireless security design" },
];

const faqData = [
  { q: "Why is a professional site survey necessary for enterprise Wi-Fi?", a: "Enterprise wireless networks must support hundreds or thousands of devices with consistent performance across diverse environments. Without a professional survey using tools like Ekahau Pro, AP placement is based on guesswork — leading to dead zones, channel interference, and capacity shortfalls. A proper Ekahau-powered design accounts for wall materials, obstructions, device density, and application requirements before any hardware is purchased." },
  { q: "What is the difference between a predictive design and a site survey?", a: "A predictive design uses RF simulation software (Ekahau Pro) to model wireless coverage on an imported floor plan before any hardware is installed. A site survey involves physically walking the space with measurement hardware (Ekahau Sidekick 2) to capture actual RF data. ALG Data Guard performs both: predictive design at the start to optimize AP placement, and a post-deployment validation survey to confirm the live network meets specification." },
  { q: "How does Ekahau Sidekick 2 differ from a laptop-based survey?", a: "Ekahau Sidekick 2 is dedicated survey hardware with two simultaneous radio chains — one for 2.4GHz and one for 5GHz — capturing both bands at the same time without switching. This makes surveys significantly faster and more accurate than a laptop adapter, which must time-share between bands. Sidekick 2 also removes interference from the laptop's own radios, delivering cleaner measurement data." },
  { q: "What floor plan format does Ekahau Pro require?", a: "Ekahau Pro accepts PDF, JPG, PNG, AutoCAD DWG/DXF, and Revit BIM files. For the most accurate design, we recommend architectural-grade PDFs with accurate dimensions. If you have digital floor plans from construction drawings, those are ideal. ALG Data Guard can work with hand-drawn sketches or rough layouts where professional drawings aren't available, though accuracy may be reduced." },
  { q: "How long does a typical Ekahau Wi-Fi design project take?", a: "A standard single-floor predictive design and AP placement report typically takes 3–5 business days from receipt of floor plans. Post-deployment validation surveys depend on facility size — a typical 50,000 sq ft building takes 4–8 hours to survey with Sidekick 2. Full-building projects including design, installation, and validation typically run 2–6 weeks depending on scope and scheduling." },
  { q: "Can Ekahau Pro design for Wi-Fi 6 and Wi-Fi 6E networks?", a: "Yes. Ekahau Pro supports Wi-Fi 6 (802.11ax) and Wi-Fi 6E AP models from all major vendors including Cisco, HPE Aruba, Juniper Mist, Extreme, and Ruckus. The simulation engine accounts for Wi-Fi 6 OFDMA, BSS Coloring, Target Wake Time, and the additional 6GHz band spectrum available in Wi-Fi 6E deployments. ALG Data Guard engineers design for current generation Wi-Fi 6/6E platforms by default." },
];

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);
const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export default function EkahauByOokla() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);

  const c1 = useCounter(60000, 2500, metricsInView);
  const c2 = useCounter(98, 2000, metricsInView);
  const c3 = useCounter(500, 2000, metricsInView);
  const c4 = useCounter(12, 2000, metricsInView);

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="ekahau-v2-page pd-page">
      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={ekahauLogo} alt="Ekahau by Ookla" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Wi-Fi Design & Site Survey Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Ekahau by Ookla</h1>
            <p className="pd-identity-desc">
              Ekahau, the world's leading Wi-Fi design and site survey platform, powers precision wireless
              network design used by 60,000+ professionals globally. ALG Data Guard's Ekahau-certified
              engineers use Ekahau Pro and Sidekick 2 to deliver wireless networks that perform exactly as designed —
              every time, in every environment.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="ek-hero">
        <div className="ek-hero__heatmap" aria-hidden="true">
          <div className="ek-hero__blob ek-hero__blob--1" />
          <div className="ek-hero__blob ek-hero__blob--2" />
          <div className="ek-hero__blob ek-hero__blob--3" />
        </div>
        <div className="ek-hero__grid" aria-hidden="true" />
        <div className="ek-hero__inner">
          <div className="ek-hero__content">
            <div className="ek-hero__kicker">
              <span className="ek-hero__kicker-dot" />
              Ekahau Certified Wi-Fi Engineering
            </div>
            <h2 className="ek-hero__title">
              Wireless Networks Designed with <em>Scientific Precision</em>
            </h2>
            <p className="ek-hero__body">
              Bad Wi-Fi is a business problem. Ekahau-powered design means every access point is placed
              exactly where signal simulation and real-world measurement data says it belongs — eliminating
              dead zones, interference, and expensive rework before your network goes live.
            </p>
            <div className="ek-hero__actions">
              <button className="ek-btn-primary" onClick={() => navigate("/contact")}>
                Get a Wi-Fi Design <ArrowIcon />
              </button>
              <button className="ek-btn-outline" onClick={() => navigate("/contact")}>
                Talk to an RF Engineer
              </button>
            </div>
            <div className="ek-hero__stats">
              <div>
                <div className="ek-hero__stat-value">60K+</div>
                <div className="ek-hero__stat-label">Ekahau Professionals Worldwide</div>
              </div>
              <div>
                <div className="ek-hero__stat-value">500+</div>
                <div className="ek-hero__stat-label">Wireless Projects Completed</div>
              </div>
              <div>
                <div className="ek-hero__stat-value">100%</div>
                <div className="ek-hero__stat-label">Post-Deployment Validated</div>
              </div>
            </div>
          </div>
          <div className="ek-hero__visual-card">
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=700&q=80"
              alt="Wi-Fi coverage heatmap visualization"
              className="ek-hero__visual-img"
            />
            <div className="ek-hero__visual-metrics">
              <div className="ek-hero__vis-metric">
                <div className="ek-hero__vis-metric-val">-65</div>
                <div className="ek-hero__vis-metric-label">dBm Signal</div>
              </div>
              <div className="ek-hero__vis-metric">
                <div className="ek-hero__vis-metric-val">867</div>
                <div className="ek-hero__vis-metric-label">Mbps Rate</div>
              </div>
              <div className="ek-hero__vis-metric">
                <div className="ek-hero__vis-metric-val">36</div>
                <div className="ek-hero__vis-metric-label">dB SNR</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Ekahau ── */}
      <section className="ek-why">
        <div className="ek-shell">
          <div className="ek-why__head">
            <span className="ek-badge"><span className="ek-dot" /> Why Ekahau</span>
            <h2 className="ek-section-title">The Standard for Professional Wi-Fi Design</h2>
            <p className="ek-section-sub">
              Ekahau by Ookla is the industry's most trusted wireless planning and survey platform —
              and ALG Data Guard uses it on every wireless project, from small offices to multi-site enterprises.
            </p>
          </div>
          <div className="ek-why__grid">
            {whyCards.map((card) => (
              <div key={card.title} className="ek-why-card">
                <div className="ek-why-card__icon">{card.icon}</div>
                <h3 className="ek-why-card__title">{card.title}</h3>
                <p className="ek-why-card__body">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tool Showcase ── */}
      <section className="ek-tools">
        <div className="ek-shell">
          <div className="ek-tools__head">
            <span className="ek-badge"><span className="ek-dot" /> Ekahau Tools</span>
            <h2 className="ek-section-title">Professional Wi-Fi Tools on Every Project</h2>
            <p className="ek-section-sub">
              ALG Data Guard uses Ekahau's professional toolset on every wireless engagement —
              from initial predictive design through to post-deployment validation and documentation.
            </p>
          </div>
          <div className="ek-tools__grid">
            {tools.map((t) => (
              <div key={t.name} className="ek-tool-card">
                <div className="ek-tool-card__img-wrap">
                  <img src={t.img} alt={t.name} className="ek-tool-card__img" />
                </div>
                <div className="ek-tool-card__body">
                  <div className="ek-tool-card__header">
                    <span className="ek-tool-card__icon">{t.icon}</span>
                    <h3 className="ek-tool-card__name">{t.name}</h3>
                  </div>
                  <p className="ek-tool-card__desc">{t.desc}</p>
                  <ul className="ek-tool-card__features">
                    {t.features.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industry Solutions ── */}
      <section className="ek-industries">
        <div className="ek-shell">
          <div className="ek-industries__head">
            <span className="ek-badge" style={{ background: "rgba(0,102,204,0.2)", borderColor: "rgba(0,170,255,0.4)", color: "#66ccff" }}>
              <span className="ek-dot" /> Industries We Serve
            </span>
            <h2 className="ek-section-title">Wireless Expertise Across Every Sector</h2>
            <p className="ek-section-sub">
              Every environment presents unique RF challenges. ALG Data Guard's Ekahau-powered
              approach delivers precision wireless design tailored to each industry's specific needs.
            </p>
          </div>
          <div className="ek-industries__grid">
            {industries.map((ind) => (
              <div key={ind.name} className="ek-industry-card">
                <span className="ek-industry-card__icon">{ind.icon}</span>
                <h3 className="ek-industry-card__name">{ind.name}</h3>
                <p className="ek-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Workflow ── */}
      <section className="ek-workflow">
        <div className="ek-shell">
          <div className="ek-workflow__inner">
            <div>
              <img
                src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=800&q=80"
                alt="Wireless network professional conducting site survey"
                className="ek-workflow__img"
              />
            </div>
            <div>
              <span className="ek-badge"><span className="ek-dot" /> Our Process</span>
              <h2 className="ek-workflow__title">Don't Guess. Measure. Design. Validate.</h2>
              <p className="ek-workflow__body">
                Too many wireless networks are designed on rules of thumb. Ekahau-powered design
                means every decision is backed by RF simulation data and real-world measurements.
                Here's how ALG Data Guard delivers wireless networks that work as designed.
              </p>
              <div className="ek-workflow__steps">
                {workflowSteps.map((step) => (
                  <div key={step.num} className="ek-workflow__step">
                    <div className="ek-workflow__step-num">{step.num}</div>
                    <div>
                      <div className="ek-workflow__step-title">{step.title}</div>
                      <p className="ek-workflow__step-desc">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="ek-btn-primary" onClick={() => navigate("/contact")}>
                Start Your Wi-Fi Project <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Deployment Timeline ── */}
      <section className="ek-timeline">
        <div className="ek-shell">
          <div className="ek-timeline__head">
            <span className="ek-badge"><span className="ek-dot" /> Deployment Process</span>
            <h2 className="ek-section-title">End-to-End Wi-Fi Delivery</h2>
            <p className="ek-section-sub">
              A structured, validated delivery process from initial requirements through to post-deployment
              sign-off and professional documentation.
            </p>
          </div>
          <div className="ek-timeline__steps">
            {deploymentSteps.map((step, i) => (
              <div key={i} className="ek-timeline__step">
                <div className="ek-timeline__num">{step.icon}</div>
                <h3 className="ek-timeline__step-title">{step.title}</h3>
                <p className="ek-timeline__step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Business Benefits ── */}
      <section className="ek-benefits">
        <div className="ek-shell">
          <div className="ek-benefits__inner">
            <div className="ek-benefits__content">
              <span className="ek-badge" style={{ background: "rgba(0,102,204,0.2)", borderColor: "rgba(0,170,255,0.4)", color: "#66ccff" }}>
                <span className="ek-dot" /> Business Value
              </span>
              <h2 className="ek-section-title">Why Ekahau-Powered Design Pays Off</h2>
              <p className="ek-section-sub">
                Professional wireless design isn't just about signal strength — it's a business investment
                that eliminates costly rework, reduces infrastructure spend, and delivers measurable ROI.
              </p>
              <div className="ek-benefits__list">
                {benefits.map((b) => (
                  <div key={b.title} className="ek-benefit-item">
                    <div className="ek-benefit-item__icon">{b.icon}</div>
                    <div>
                      <div className="ek-benefit-item__title">{b.title}</div>
                      <p className="ek-benefit-item__desc">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80"
                alt="Enterprise network operations"
                className="ek-benefits__img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="ek-metrics" ref={metricsRef}>
        <div className="ek-shell">
          <div className="ek-metrics__inner">
            <div>
              <div className="ek-metric__value">{c1.toLocaleString()}+</div>
              <div className="ek-metric__label">Ekahau Engineers Worldwide</div>
            </div>
            <div>
              <div className="ek-metric__value">{c2}%</div>
              <div className="ek-metric__label">Customer Satisfaction Rate</div>
            </div>
            <div>
              <div className="ek-metric__value">{c3}+</div>
              <div className="ek-metric__label">Wi-Fi Projects Delivered</div>
            </div>
            <div>
              <div className="ek-metric__value">{c4}+</div>
              <div className="ek-metric__label">Years of RF Engineering</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="ek-certs">
        <div className="ek-shell">
          <div className="ek-certs__head">
            <span className="ek-badge"><span className="ek-dot" /> Our Credentials</span>
            <h2 className="ek-section-title">Certified Wi-Fi Engineers</h2>
            <p className="ek-section-sub">
              ALG Data Guard's wireless team holds the industry's most recognized Wi-Fi certifications,
              ensuring every engagement is led by qualified, actively practicing RF engineers.
            </p>
          </div>
          <div className="ek-certs__grid">
            {certifications.map((c) => (
              <div key={c.name} className="ek-cert-card">
                <div className="ek-cert-card__badge">{c.badge}</div>
                <div className="ek-cert-card__name">{c.name}</div>
                <div className="ek-cert-card__desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="ek-faq">
        <div className="ek-shell">
          <div className="ek-faq__head">
            <span className="ek-badge" style={{ background: "rgba(0,102,204,0.2)", borderColor: "rgba(0,170,255,0.4)", color: "#66ccff" }}>
              <span className="ek-dot" /> Frequently Asked Questions
            </span>
            <h2 className="ek-section-title">Wi-Fi Design Questions, Answered</h2>
            <p className="ek-section-sub">
              Everything you need to know about professional Ekahau-powered wireless design and how ALG Data Guard delivers it.
            </p>
          </div>
          <div className="ek-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`ek-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="ek-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="ek-faq-item__q">{faq.q}</span>
                  <span className="ek-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="ek-faq-item__answer">
                  <div className="ek-faq-item__answer-inner">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ek-cta">
        <div className="ek-cta__inner">
          <span className="ek-badge" style={{ background: "rgba(0,102,204,0.25)", borderColor: "rgba(0,170,255,0.5)", color: "#66ccff", margin: "0 auto 1.2rem" }}>
            <span className="ek-dot" /> Get Started
          </span>
          <h2 className="ek-cta__title">
            Get a Professionally Designed Wi-Fi Network with ALG Data Guard
          </h2>
          <p className="ek-cta__sub">
            Our Ekahau-certified RF engineers will design, deploy, and validate a wireless network
            that performs consistently across every corner of your facility — backed by professional
            documentation and post-deployment proof of performance.
          </p>
          <div className="ek-cta__actions">
            <button className="ek-btn-primary" onClick={() => navigate("/contact")}>
              Request a Wi-Fi Design <ArrowIcon />
            </button>
            <button className="ek-btn-outline" onClick={() => navigate("/contact")}>
              Talk to an RF Engineer
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
